import { faCalculator, faList, faSave, faWallet } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "components/common/button/Button";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import Input from "components/common/form/inputs/Input";
import Page from "components/common/page/Page";
import { graph } from "config/axios-context";
import { AppRoutes } from "config/router";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { saleDatalistQuery } from "types/agriculture/sale-types";
import { DataInfoType } from "types/datalist-types";
import { SaleDatalistQuery, SaleDatalistQueryVariables, SaleFilterInput, SaleMultiUpdateDocument, SaleMultiUpdateInput, SaleMultiUpdateMutation, SaleMultiUpdateMutationVariables, SaleSortInput, SortEnumType } from "types/graphql";
import { appendFilters, DatalistFilterDefinitions } from "utilities/datalist/datalist-filter-utilities";
import { formatMoney } from "utilities/datalist/datalist-sort-utilities";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import SaleMultiupdatePageCardView from "./partial/SaleMultiupdatePageCardView";
import { DatalistColumnDefinitions } from "utilities/datalist/datalist-utilities";
import { uuid } from "config/app-constants";
import WalletDropdown from "../../../components/entities/app/person/wallet/WalletDropdown";

type ListItem = Exclude<Exclude<Exclude<SaleDatalistQuery['sales'], null | undefined>['items'], null | undefined>[0], null | undefined>;
type FormItem = {
	realKg: number,
	kg: string | number,
	pricePerKg: string | number,
	total: string | number,
	cartCount: number,
	wallet?: {
		id: number,
		name: string
	} | null
}

export const TOTAL_ROW_INDEX = -1;

const SaleMultiUpdatePage = () => {
	const [t] = useTranslation();
	const [title, setTitle] = useState<string>(t('pages.agriculture.sale.multi-update'));
	const context = useAppContext();
	const datalistRef = useRef<Datalist<ListItem, SaleFilterInput, SaleSortInput>>(null);
	const [model, setModel] = useState<Record<number, FormItem>>({});
	const [kgPerCart, setKgPerCart] = useState<string>('');

	const datalistConfig: DatalistPropsType<ListItem, SaleFilterInput, SaleSortInput> = {
		itemId: i => i.id,
		name: 'sale-datalist-page',
		dataSource: {
			request: async (params) => {
				var variables: SaleDatalistQueryVariables = {
					order: params.sort,
					where: params.filters
				}

				if (params.searchText) {
					const searchText: SaleFilterInput = {
						or: [
							{
								person: {
									name: {
										contains: params.searchText
									}
								}
							},
							{
								category: {
									name: {
										contains: params.searchText
									}
								}
							},
							{
								name: {
									contains: params.searchText
								}
							},
							{
								season: {
									name: {
										contains: params.searchText
									}
								}
							}
						]
					};

					variables.where = appendFilters(variables.where, searchText);
				}

				variables.where = appendFilters(variables.where, {
					total: {
						eq: 0
					}
				});

				const response = await graph<SaleDatalistQuery, SaleDatalistQueryVariables>({
					query: saleDatalistQuery,
					variables
				});

				if (response.errors) {
					throw response.errors
				}

				setTitle(t('pages.agriculture.sale.multi-update') + ": " + (response.data?.sales?.items?.length || 0));

				if (response.data?.sales?.items?.length) {
					response.data?.sales?.items?.push({
						id: TOTAL_ROW_INDEX,
						kg: response.data?.sales?.items.reduce((a, b) => a + (b?.kg ?? 0), 0),
						pricePerKg: '',
						total: response.data?.sales?.items.reduce((a, b) => a + (b?.total ?? 0), 0),
						person: {
							id: 0,
							name: t('fields.total.name')
						},
						category: {
							id: 0,
							name: ''
						},
						cartQuantity: response.data?.sales?.items.reduce((a, b) => a + (b?.cartQuantity ?? 0), 0),
						chamber: false,
						date: null,
						season: {
							id: -1,
							name: 'Active'
						},
						fruit: {
							id: 0,
							name: ''
						}
					});
				}

				const dataModel: Record<number, FormItem> = {};
				response.data?.sales?.items?.forEach(i => {
					if (!i) return;
					dataModel[i.id] = {
						realKg: i.kg,
						kg: i.kg || '',
						pricePerKg: i.pricePerKg || '',
						total: i.total || '',
						cartCount: i.cartQuantity,
						wallet: i.wallet
					};
				});

				setModel(dataModel);
				return response.data!.sales! as DataInfoType<ListItem>;
			},
			sort: [{
				name: 'date',
				direction: SortEnumType.Asc,
				id: uuid()
			}],
			filter: {
				id: uuid(),
				logic: 'and',
				filters: [DatalistFilterDefinitions.seasonFilterDefinition(context)]
			}
		},
		table: {
			columns: [
				{
					name: 'id',
					resolve: i => i.id,
					title: 'fields.id.name',
					hide: true
				},
				{
					name: 'date',
					title: 'fields.date.name',
					resolve: i => i.id === TOTAL_ROW_INDEX ? t('fields.total.name') : DateUtil.format(new Date(i.date), DateFormats.SHORT),
					filter: {
						type: 'date'
					},
					sort: true
				},
				DatalistColumnDefinitions.personColumnDefinition(context),
				{
					name: 'kg',
					title: 'fields.kg.shortName',
					resolve: i => {
						return <Input
							name="kg"
							hideErrorMessages
							selectTextOnFocus
							type="number"
							required={true}
							value={i.id === TOTAL_ROW_INDEX ? kgPerCart : model[i.id].kg?.toString()}
							onBlur={v => {
								if (i.id === TOTAL_ROW_INDEX) setKgPerCart('');
								else if (!v || v === '0') onChangeValue(i.id, { kg: '' })
							}}
							onChange={v => {
								if (i.id === TOTAL_ROW_INDEX) {
									setKgPerCart(v || '');
									const nr = Number(v);
									if (nr) onKgPerCartChange(nr);
								}
								else onChangeValue(i.id, { kg: v });
							}}
							suffix={{ icon: faCalculator, onClick: () => calculateKg(i.id), name: t('common.calculate'), hidden: () => i.id === TOTAL_ROW_INDEX }}
							placeholder="fields.kg.shortName"
						/>
					},
					filter: {
						type: 'number'
					},
					sort: true
				},
				{
					name: 'pricePerKg',
					title: 'fields.price-per-kg.shortName',
					filter: {
						type: 'number'
					},
					sort: true,
					resolve: i => {
						return <Input
							name="pricePerKg"
							type="number"
							hideErrorMessages
							selectTextOnFocus
							onBlur={() => i.id === TOTAL_ROW_INDEX && onChangeValue(TOTAL_ROW_INDEX, { pricePerKg: '' })}
							required={i.id !== TOTAL_ROW_INDEX}
							value={model[i.id]?.pricePerKg?.toString()}
							onChange={v => onChangeValue(i.id, { pricePerKg: v })}
							placeholder="fields.price-per-kg.shortName"
						/>
					}
				},
				{
					name: 'total',
					title: 'fields.total.shortName',
					filter: {
						type: 'number'
					},
					sort: true,
					resolve: i => {
						return i.id === TOTAL_ROW_INDEX ? formatMoney(model[i.id].total) : <Input
							name="total"
							type="number"
							required={true}
							hideErrorMessages
							selectTextOnFocus
							value={model[i.id]?.total?.toString()}
							onChange={v => onChangeValue(i.id, { total: v })}
							placeholder="fields.total.name"
							onFocus={() => calculate(i.id, false)}
							suffix={{ icon: faCalculator, onClick: () => calculate(i.id, true), name: t('common.calculate') }}
						/>
					}
				},
				{
					name: 'wallet',
					title: 'fields.wallet.shortName',
					filter: {
						type: 'reference'
					},
					resolve: i => {
						return i.id === TOTAL_ROW_INDEX ? '' : <WalletDropdown
							name={"wallet-" + i.id}
							value={model[i.id].wallet || undefined}
							personId={i.person.id}
							label={false}
							prefix={{ icon: faWallet, name: 'wallet' }}
							onChange={v => onChangeValue(i.id, { wallet: v })}
						/>
					}
				},
				{
					name: 'cartQuantity',
					title: 'fields.cart-quantity.shortName',
					filter: {
						type: 'number'
					},
					sort: true,
					resolve: i => i.cartQuantity
				},
				{
					name: 'category',
					title: 'fields.category.shortName',
					filter: {
						type: 'reference'
					},
					sort: true,
					resolve: i => i.id === TOTAL_ROW_INDEX ? '' : t('agriculture.category.' + i.category!.name.toLowerCase())
				},
				DatalistColumnDefinitions.fruitColumnDefinition(context),
				DatalistColumnDefinitions.seasonColumnDefinition(context),
				{
					name: 'createdAt',
					title: '',
					resolve(i) {
						return <Button onClick={() => save(i.id)} theme="link primary" title={t('button.save')}>
							<FontAwesomeIcon icon={faSave} />
						</Button>
					},
				}
			],
			render: {
				card: i => {
					const className = i.id === TOTAL_ROW_INDEX ? 'bg-neutral-200 dark:bg-neutral-800' : '';
					return (
						<div key={'sales-datalist-card-' + i.id} className={`w-full border theme-border px-4 py-2 mb-4 hover:border-sunrise-300 ${className}`.trim()}>
							<SaleMultiupdatePageCardView
								calculateKg={calculateKg}
								kgPerCart={kgPerCart}
								setKgPerCart={setKgPerCart}
								onKgPerCartChange={onKgPerCartChange}
								model={i as any}
								name={'sale-view-datalist-multiupdate-view-' + i.id}
								formModel={model[i.id]}
								onChangeValue={onChangeValue}
								calculate={calculate}
								onSave={save}
							/>
						</div>
					);
				}
			}
		}
	};

	const pageActions = [
		{
			icon: faList,
			link: AppRoutes.AGRICULTURE_OVERVIEW_SALE,
			name: 'list',
			title: t('pages.agriculture.sale.list')
		}
	]

	const onKgPerCartChange = (value: number) => {
		for (const key in model) {
			if (Object.prototype.hasOwnProperty.call(model, key)) {
				const element = model[key];
				if (!element.realKg || !element.kg) {
					element.kg = (element.cartCount * value).toFixed(0);
					if (Number(element.pricePerKg) > 0) {
						element.total = (Number(element.kg) * Number(element.pricePerKg)).toFixed(2);
					}
				}
			}
		}

		updateData(model);
		datalistRef.current?.refresh();
	}

	const onChangeValue = (id: number, m: Partial<FormItem>) => {
		model[id] = {
			...model[id],
			...m
		};

		if (id === TOTAL_ROW_INDEX && m.pricePerKg && Number(m.pricePerKg) > 0) {
			for (const key in model) {
				//- needed because key can be int or string
				if (key == TOTAL_ROW_INDEX?.toString()) {
					continue;
				}

				model[key] = {
					...model[key],
					pricePerKg: m.pricePerKg,
					total: (parseFloat(model[key].kg?.toString() || '0') * parseFloat(m.pricePerKg?.toString() || '0')).toFixed(2)
				}
			}
		}

		if (id !== TOTAL_ROW_INDEX && 'kg' in m && !m.kg) {
			model[id] = {
				...model[id],
				realKg: 0,
			}
		}

		updateData(model);
		datalistRef.current?.refresh();
	}

	const updateData = (dataModel?: Record<number, FormItem>) => {
		dataModel = dataModel || model;
		dataModel[TOTAL_ROW_INDEX] = {
			...model[TOTAL_ROW_INDEX],
			kg: calculateField(model, 'kg').toFixed(2),
			total: calculateField(model, 'total').toFixed(2)
		};

		setModel(dataModel);
	}

	const calculateKg = (id: number) => {
		if (Number(model[id].cartCount)) {
			model[id].kg = Number(model[id].kg) * Number(model[id].cartCount);
			onChangeValue(id, { kg: model[id].kg });
		}
	}

	const calculate = (id: number, force: boolean) => {
		if (!force && Number(model[id].total)) return;
		model[id].total = (Number(model[id].kg) || 0) * (Number(model[id].pricePerKg) || 0);
		model[id].total = model[id].total ? Number(model[id].total).toFixed(2) : model[id].total;
		onChangeValue(id, { total: model[id].total });
	}

	const calculateField = (items: Record<number, FormItem>, key: keyof FormItem): number => {
		let total = 0;
		for (const item in items) {
			if (item === TOTAL_ROW_INDEX?.toString()) continue;
			total += Number(items[item][key] || 0);
		}

		return total;
	}

	const save = async (id: number) => {
		let data: SaleMultiUpdateInput[] = [];
		if (id !== TOTAL_ROW_INDEX) {
			data.push({
				id: Number(id),
				kg: Number(model[id].kg),
				pricePerKg: Number(model[id].pricePerKg),
				total: Number(model[id].total),
				walletId: model[id].wallet?.id
			});
		} else {
			for (const key in model) {
				if (key === TOTAL_ROW_INDEX?.toString()) continue;
				data.push({
					id: Number(key),
					kg: Number(model[key].kg),
					pricePerKg: Number(model[key].pricePerKg),
					total: Number(model[key].total),
					walletId: model[key].wallet?.id
				});
			}
		}

		data = data.filter(i => i.kg > 0 && i.pricePerKg > 0 && i.total > 0);
		if (data.length === 0) {
			context.notify('info', t('common.no-data-to-save'));
			return;
		}

		try {
			const response = await graph<SaleMultiUpdateMutation, SaleMultiUpdateMutationVariables>({
				query: SaleMultiUpdateDocument,
				variables: {
					data
				}
			});

			if (response.errors) {
				throw response.errors;
			}

			context.notify('success', t('form.submit.success'));
			datalistRef.current?.reload();
		} catch (e) {
			context.notify('error', t('form.submit.error'));

		}
	}

	return (
		<Page wFull title={title} actions={pageActions}>
			<Datalist ref={datalistRef}
				{...datalistConfig}
			/>
		</Page>
	);
};

export default SaleMultiUpdatePage;
